"use client";

import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";

interface BannerView {
	image: string;
	imageMobile: string | null;
	title: string | null;
	subtitle: string | null;
	ctaText: string | null;
	href: string | null;
}

/**
 * Module scope, not inside `HeroCarousel`: defined there it minted a NEW
 * component type per render, so every slide's DOM subtree remounted on each
 * auto-slide tick.
 */
function Slide({ b }: { b: BannerView }) {
	const content = (
		<>
			<picture>
				{b.imageMobile && <source media="(max-width: 640px)" srcSet={b.imageMobile} />}
				{}
				<img src={b.image} alt={b.title ?? ""} className="h-full w-full object-cover" />
			</picture>
			{(b.title || b.subtitle || b.ctaText) && (
				<div className="absolute inset-0 flex flex-col items-start justify-end gap-1.5 bg-gradient-to-t from-black/60 via-black/10 to-transparent p-5 sm:p-8">
					{b.title && (
						<h2 className="max-w-[70%] text-lg font-bold text-white drop-shadow sm:text-3xl">
							{b.title}
						</h2>
					)}
					{b.subtitle && (
						<p className="max-w-[70%] text-xs text-white/90 drop-shadow sm:text-base">
							{b.subtitle}
						</p>
					)}
					{b.ctaText && (
						<span className="mt-1 inline-block rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground sm:text-sm">
							{b.ctaText}
						</span>
					)}
				</div>
			)}
		</>
	);
	return b.href ? (
		<a href={b.href} className="relative block h-full w-full">
			{content}
		</a>
	) : (
		<div className="relative h-full w-full">{content}</div>
	);
}

/**
 * Hero banner / banner group (البند 12.3). A single banner renders as a static
 * hero; multiple render as a carousel with dots, desktop arrows, mobile swipe
 * and optional auto-slide. The FULL banner is clickable — the CTA button is an
 * extra visual action only.
 */
export function HeroCarousel({
	banners,
	autoSlide = true,
	slideSeconds = 5,
}: {
	banners: BannerView[];
	autoSlide?: boolean;
	slideSeconds?: number;
}) {
	const [i, setI] = useState(0);
	const n = banners.length;
	const touchXRef = useRef<number | null>(null);

	useEffect(() => {
		if (n <= 1 || !autoSlide) return;
		const id = setInterval(() => setI((v) => (v + 1) % n), Math.max(2, slideSeconds) * 1000);
		return () => clearInterval(id);
	}, [n, autoSlide, slideSeconds]);

	if (n === 0) return null;
	const go = (next: number) => setI(((next % n) + n) % n);

	return (
		<section className="relative border-b border-border bg-surface-2">
			<div
				className="relative mx-auto aspect-[16/8] max-h-[420px] w-full max-w-6xl overflow-hidden sm:aspect-[16/5]"
				onTouchStart={(e) => {
					touchXRef.current = e.touches[0]?.clientX ?? null;
				}}
				onTouchEnd={(e) => {
					if (touchXRef.current == null || n <= 1) return;
					const dx = (e.changedTouches[0]?.clientX ?? touchXRef.current) - touchXRef.current;
					touchXRef.current = null;
					if (Math.abs(dx) > 40) go(dx > 0 ? i - 1 : i + 1);
				}}
			>
				<div
					className="flex h-full w-full transition-transform duration-500 ease-out"
					style={{ transform: `translateX(${i * -100}%)` }}
					dir="ltr"
				>
					{banners.map((b, idx) => (
						<div key={idx} className="h-full w-full shrink-0">
							<Slide b={b} />
						</div>
					))}
				</div>

				{n > 1 && (
					<>
						{/* Prev/next arrows — desktop only; mobile uses swipe (12.3.3). */}
						<button
							type="button"
							onClick={() => go(i - 1)}
							aria-label="Previous"
							className="absolute start-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white backdrop-blur transition hover:bg-black/60 sm:grid"
						>
							<Icon icon={faChevronLeft} size={14} />
						</button>
						<button
							type="button"
							onClick={() => go(i + 1)}
							aria-label="Next"
							className="absolute end-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white backdrop-blur transition hover:bg-black/60 sm:grid"
						>
							<Icon icon={faChevronRight} size={14} />
						</button>
						<div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
							{banners.map((_, idx) => (
								<button
									key={idx}
									type="button"
									onClick={() => go(idx)}
									aria-label={`Slide ${idx + 1}`}
									className={`h-2 rounded-full transition-all ${
										idx === i ? "w-5 bg-white" : "w-2 bg-white/50"
									}`}
								/>
							))}
						</div>
					</>
				)}
			</div>
		</section>
	);
}
